import type { AccountDetail } from "../app/account-model.js";
import type { Cell, HomeModel } from "../app/home-model.js";
import { DUE_COPY, LAPSED_COPY } from "../app/kyc-model.js";
import { LOCKED_HOME } from "../app/security-model.js";
import { calendarDate } from "../lib/format.js";
import { el } from "../lib/dom.js";
import { keepTogether } from "../lib/keep.js";
import { icon, type IconId } from "../lib/icon.js";
import { DetailView, type DetailHandlers } from "./account.js";
import type { AimHandlers } from "./aim.js";
import { tabBar, type Tab } from "./tabs.js";

/**
 * Home (UX spec v3 §C.4, mock-up 01-home.html): a fixed top bar (the customer's
 * initials and name; a hairline once content passes under it), then the one
 * scroller — "Total in accounts", one cell per product (the figure first, the name
 * with its digits, then only what the product carries), Recent with "See all" — and
 * the tab bar with the assistant's mark, the one way into the assistant.
 *
 * States: loading (the same cells with their words as bars of the same line-heights,
 * one "Loading balances" status — nothing moves when the balances land), default, and
 * stale / unreachable (the banner and Retry; the last balances stay).
 *
 * The bell is not drawn: no notification read exists (README deviations). The joint
 * account's co-holder ("with Mdm Lim") is not printed: the account read does not carry
 * it (README ask hh). Recent rows are not controls; "See all" is Activity.
 */
export interface HomeHandlers {
  readonly assistant: AimHandlers;
  onRetry(): void;
  onTab(tab: Tab): void;
  /** A cell's detail (13); absent = cells are not controls. */
  onOpen?(key: string): void;
  /** The detail's back, actions and "See all in Activity" (13). */
  readonly detail?: DetailHandlers;
  /** DCTA-231 V2: the lock banner's Unlock — Me › Security, as Invest's (absent: the banner says it, with no action). */
  onUnlock?(): void;
}

/** The shape the skeleton draws before the first read: Home's usual products, so the heights match. */
const SKELETON: readonly Cell[] = [
  { key: "sk1", kind: "account", icon: "i-bank", amount: "S$00,000.00", name: "Everyday Savings", digits: "···0000", cards: [{ digits: "0000", kind: "debit" }] },
  { key: "sk2", kind: "joint", icon: "i-users", amount: "S$00,000.00", name: "Joint Account", digits: "···0000", cards: [] },
  {
    key: "sk3",
    kind: "credit",
    icon: "i-card",
    amount: "S$000.00",
    name: "Platinum Card",
    digits: "···0000 · owed",
    cards: [{ digits: "0000", kind: "credit" }],
    due: { amount: "S$00.00", by: "by 00 Sep" },
    meter: { available: "S$00,000.00", limit: "S$00,000.00", used: "0%" },
  },
  {
    key: "sk4",
    kind: "invest",
    icon: "i-trend",
    amount: "S$0,000.00",
    name: "Investments",
    digits: "· 0 holdings",
    cards: [],
    pill: { text: "+00.0%", tone: "up" },
    gain: "+S$0,000.00",
    settlement: { key: "sk5", name: "Trading settlement (SGD)", digits: "···0000", amount: "S$0,000.00" },
  },
];

/** A titled banner (26a's component): its icon, the title over its line, one action — nothing closes it. Shared with Invest. */
export function titledBanner(o: {
  readonly cls: string;
  readonly icon: IconId;
  readonly title: string;
  readonly line: readonly (Node | string)[];
  readonly action: string;
  readonly actionCls: string;
  readonly onAction: () => void;
}): HTMLElement {
  const act = el("button", { class: o.actionCls, type: "button" }, [o.action]);
  act.addEventListener("click", o.onAction);
  return el("div", { class: o.cls, role: "status" }, [icon(o.icon), el("span", { class: "bt" }, [el("b", {}, [o.title]), el("span", {}, o.line)]), act]);
}

/** The identity check's Home banner (26a; P1-5): the id card, the title over its line, one "Update now". */
function identityBanner(title: string, line: readonly (Node | string)[], action: string, onUpdate: () => void): HTMLElement {
  return titledBanner({ cls: "banner dueban", icon: "i-id", title, line, action, actionCls: "raw upd", onAction: onUpdate });
}

export class HomeView {
  readonly parts: readonly HTMLElement[];
  readonly mark: HTMLButtonElement;
  readonly body: HTMLElement;
  readonly #handlers: HomeHandlers;
  readonly #topbar: HTMLElement;
  readonly #banner: HTMLElement;
  readonly #bannerText: HTMLElement;
  readonly #note: HTMLElement;
  readonly #home: HTMLElement;
  readonly #detailScr: HTMLElement;
  readonly detail: DetailView;
  /** What opened the detail: a cell's key (cells are rebuilt on every refresh), or the focused element. */
  #opener: string | HTMLElement | undefined;
  /** DCTA-176 (26a, F48): the identity check is due — the calm banner, until the check is done. */
  #due: HTMLElement | undefined;
  /** Umakhan 13 Oct (P0): the watch on the first tile's height, while its cells are on screen. */
  #tiles: ResizeObserver | undefined;

  constructor(handlers: HomeHandlers) {
    this.#handlers = handlers;
    this.#bannerText = el("span");
    const retry = el("button", { class: "raw", type: "button" }, ["Retry"]);
    retry.addEventListener("click", () => this.#handlers.onRetry());
    this.#banner = el("div", { class: "banner", role: "status", hidden: true }, [
      el("span", { class: "bic", aria: { hidden: "true" } }, [icon("i-alert")]),
      this.#bannerText,
      retry,
    ]);
    this.#note = el("span", { class: "vis-hidden", role: "status" });
    this.#topbar = el("div", { class: "topbar home" });
    this.body = el("div", { class: "body" });
    this.body.addEventListener("scroll", () => this.#hairline(), { passive: true });
    const bar = tabBar("home", { onTab: (t) => this.#handlers.onTab(t), ...this.#handlers.assistant });
    this.mark = bar.mark;
    // Home and its detail are two screens in one stack (§C.5, §C.7): the detail pushes in from the
    // right while Home slides a quarter left under a veil; the tab bar stays — the detail lives in Home.
    this.detail = new DetailView(handlers.detail ?? { onBack: () => undefined, onAction: () => undefined, onSeeAll: () => undefined });
    this.#home = el("div", { class: "scr s-home" }, [this.#topbar, this.body, el("span", { class: "veil", aria: { hidden: "true" } })]);
    this.#detailScr = el("div", { class: "scr s-detail off-r", role: "region", aria: { labelledby: this.detail.titleId, hidden: "true" } }, [this.detail.topbar, this.detail.body]);
    this.#detailScr.inert = true;
    this.parts = [el("div", { class: "screens" }, [this.#home, this.#detailScr]), bar.dock];
  }

  /** The detail is on screen. */
  get detailOpen(): boolean {
    return !this.#detailScr.classList.contains("off-r");
  }

  /** Push the detail in (its hero at once; `loading` masks the rest until `showDetail`). */
  push(d: AccountDetail, loading: boolean): void {
    // Safari does not focus a button on a tap: the cell names itself (below); else what has focus.
    this.#opener ??= document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    if (loading) this.detail.loading(d);
    else this.detail.show(d);
    this.detail.body.scrollTop = 0;
    this.#detailScr.classList.remove("off-r");
    this.#home.classList.add("under");
    this.#swap(true);
    this.detail.back.focus({ preventScroll: true });
  }

  showDetail(d: AccountDetail): void {
    this.detail.show(d);
  }

  /** Back (the button, the system's Back): Home again, focus on the cell that opened it. */
  pop(): void {
    if (!this.detailOpen) return;
    this.#detailScr.classList.add("off-r");
    this.#home.classList.remove("under");
    this.#swap(false);
    const back = typeof this.#opener === "string" ? [...this.body.querySelectorAll<HTMLElement>(".acct")].find((x) => x.dataset["go"] === this.#opener) : this.#opener;
    back?.focus({ preventScroll: true });
    this.#opener = undefined;
  }

  #swap(detail: boolean): void {
    this.#home.inert = detail;
    this.#detailScr.inert = !detail;
    if (detail) {
      this.#home.setAttribute("aria-hidden", "true");
      this.#detailScr.removeAttribute("aria-hidden");
    } else {
      this.#home.removeAttribute("aria-hidden");
      this.#detailScr.setAttribute("aria-hidden", "true");
    }
  }

  /**
   * DCTA-176 (§F.2 26a, F48): while the identity check is due (`update_by`, B59) — a calm banner on Home: the id card,
   * "Your identity check is due", until when payments keep working (the date never breaks across lines), and "Update now".
   * Not dismissible and never a toast: it goes when the check is done (a date the customer needs does not go by itself).
   * Lapsed customers get their own words on the same banner (`lapsed`, P1-5) and hear why in the chat (24a). Drawn on the
   * next paint of Home.
   */
  due(updateBy: string | undefined, onUpdate: () => void): void {
    if (updateBy === undefined) {
      this.#due = undefined;
      return;
    }
    const [before, date, after] = DUE_COPY.until(calendarDate(updateBy));
    this.#due = identityBanner(DUE_COPY.title, [before, el("span", { class: "nw" }, [date]), after], DUE_COPY.update, onUpdate);
  }

  /**
   * P1-5 (DCTA-192): while the identity check has lapsed — the due banner's component, calm, never red or dismissible: "Your
   * identity check has lapsed", that payments are stopped until it is updated, and "Update now". The chat still says why
   * (24a). Drawn on the next paint of Home; `due()` replaces it.
   */
  lapsed(onUpdate: () => void): void {
    this.#due = identityBanner(LAPSED_COPY.title, [LAPSED_COPY.line], LAPSED_COPY.update, onUpdate);
  }

  /** The same cells with their words as bars (aria-hidden), and one status for a screen reader. */
  loading(name: string, initials: string): void {
    this.#top(name, initials);
    this.#note.textContent = "Loading balances";
    this.body.replaceChildren(
      this.#banner,
      ...(this.#due === undefined ? [] : [this.#due]),
      this.#note,
      el("div", { class: "total slim skel-mask", aria: { hidden: "true" } }, [el("small", {}, ["Total in accounts"]), el("div", { class: "big money" }, ["S$00,000.00"])]),
      this.#hold(el("div", { class: "cells skel-mask", aria: { hidden: "true" } }, SKELETON.map((c) => this.#cell(c, false)))),
      el("div", { class: "sec", aria: { hidden: "true" } }, [el("h3", {}, ["Recent"])]),
      el("div", { class: "list skel-mask", aria: { hidden: "true" } }, [0, 1, 2].map(() => row("·", "Recent payment", "00 Sep · Account", "S$00.00", ""))),
      el("div", { class: "home-end", aria: { hidden: "true" } }),
    );
    this.#hairline();
  }

  show(m: HomeModel): void {
    this.#banner.hidden = true;
    this.#note.textContent = "";
    this.#top(m.name, m.initials);
    const blocks: HTMLElement[] = [
      this.#banner,
      // DCTA-231 V2: the customer locked their money — first on Home, calm, never dismissible; the balances stay below it.
      ...(m.locked ? [this.#lockBanner()] : []),
      ...(this.#due === undefined ? [] : [this.#due]),
      this.#note,
      el("div", { class: "total slim" }, [el("small", {}, ["Total in accounts"]), el("div", { class: "big money" }, [m.total])]),
      this.#hold(el("div", { class: "cells" }, m.cells.map((c) => this.#cell(c, this.#handlers.onOpen !== undefined)))),
    ];
    if (m.recent.length > 0) {
      const all = el("button", { class: "raw more", type: "button" }, ["See all"]);
      all.addEventListener("click", () => this.#handlers.onTab("activity"));
      blocks.push(
        el("div", { class: "sec" }, [el("h3", {}, ["Recent"]), all]),
        el(
          "div",
          { class: "list recent" },
          m.recent.map((r) =>
            row(r.initial, r.name, r.sub, r.figure, r.credit ? "pos" : r.muted ? "muted" : "", r.status),
          ),
        ),
      );
    }
    blocks.push(el("div", { class: "home-end", aria: { hidden: "true" } }));
    this.body.replaceChildren(...blocks);
    this.#hairline();
  }

  /** DCTA-231 V2: the lock banner — the bank's lock sentence as title and line, and Unlock (Me › Security) when there is one. */
  #lockBanner(): HTMLElement {
    const onUnlock = this.#handlers.onUnlock;
    if (onUnlock !== undefined) {
      return titledBanner({ cls: "banner lock", icon: "i-lock", title: LOCKED_HOME.title, line: [LOCKED_HOME.line], action: "Unlock", actionCls: "raw", onAction: () => onUnlock() });
    }
    return el("div", { class: "banner lock", role: "status" }, [icon("i-lock"), el("span", { class: "bt" }, [el("b", {}, [LOCKED_HOME.title]), el("span", {}, [LOCKED_HOME.line])])]);
  }

  /** A refresh failed: the last balances stay, with the time they were read. */
  stale(asOf: string): void {
    this.#bannerText.textContent = `Couldn't refresh. Balances as of ${asOf}.`;
    this.#banner.hidden = false;
  }

  /** The first read failed: there are no balances to keep, so no "as of" to claim (09's line). */
  unreachable(): void {
    this.#bannerText.textContent = "Can't reach the bank right now. Nothing has changed.";
    this.#banner.hidden = false;
  }

  #top(name: string, initials: string): void {
    this.#topbar.replaceChildren(el("span", { class: "avatar", aria: { hidden: "true" } }, [initials]), el("span", { class: "t" }, [name]));
  }

  /**
   * Umakhan 13 Oct (P0; docs/design/bug-reports/2026-10-13-home-tiles-height.jpg): no tile is shorter than Home's first — the
   * savings account, first by rank ("Everyday Savings") — so a tile that carries less (the joint account: no card) is never
   * thinner. The first tile's own height, read whenever it changes (the width, the type size, the fonts arriving), is the
   * others' min-height; app.css 76 centres what they carry. The first tile is not held by it, so it never holds itself.
   */
  #hold(cells: HTMLElement): HTMLElement {
    this.#tiles?.disconnect();
    this.#tiles = undefined;
    const first = cells.firstElementChild;
    if (first instanceof HTMLElement && typeof ResizeObserver === "function") {
      this.#tiles = new ResizeObserver(([entry]) => {
        cells.style.setProperty("--acct-min", `${String(entry?.borderBoxSize?.[0]?.blockSize ?? first.offsetHeight)}px`);
      });
      this.#tiles.observe(first);
    }
    return cells;
  }

  /** The top bar takes a hairline once content passes under it (§C.4). */
  #hairline(): void {
    this.#topbar.dataset["scrolled"] = this.body.scrollTop > 4 ? "true" : "false";
  }

  #cell(c: Cell, pressable: boolean): HTMLElement {
    const extra: HTMLElement[] = [];
    if (c.due !== undefined) {
      extra.push(el("span", { class: "due" }, ["Make a minimum payment of ", el("span", { class: "money" }, [c.due.amount]), " ", el("span", { class: "nw" }, [c.due.by])]));
    }
    const minis = c.cards.length === 0 ? undefined : el("span", { class: "minis" }, c.cards.map(thumb));
    if (c.meter !== undefined) {
      extra.push(el("span", { class: "cardrow" }, [...(minis === undefined ? [] : [minis]), meter(c.meter)]));
    } else if (minis !== undefined) {
      extra.push(minis);
    }
    if (c.gain !== undefined) extra.push(el("span", { class: "sub" }, [el("span", { class: "money" }, [c.gain]), " all time"]));
    if (c.settlement !== undefined) {
      extra.push(
        el("span", { class: "inrow" }, [
          el("span", {}, [c.settlement.name, " ", el("span", { class: "d" }, [c.settlement.digits])]),
          el("b", { class: "money" }, [c.settlement.amount]),
        ]),
      );
    }
    const parts: HTMLElement[] = [
      el("span", { class: "ic", aria: { hidden: "true" } }, [icon(c.icon)]),
      el("span", { class: "a money" }, [c.amount]),
      ...(c.pill === undefined ? [] : [el("span", { class: "t" }, [el("span", { class: PILL[c.pill.tone] }, [c.pill.text])])]),
      el("span", { class: "n" }, [c.name, " ", el("span", { class: "d" }, [c.digits])]),
      ...(extra.length === 0 ? [] : [el("span", { class: "x" }, extra)]),
    ];
    if (!pressable) return el("div", { class: "acct", data: { go: c.key } }, parts);
    const b = el("button", { class: "raw acct", type: "button", data: { go: c.key } }, parts);
    b.addEventListener("click", () => {
      this.#opener = c.key;
      this.#handlers.onOpen?.(c.key);
    });
    return b;
  }
}

/** A card thumbnail: flat, the card's own colour, its last four (§C.4 `.mc`). */
export function thumb(card: Cell["cards"][number]): HTMLElement {
  return el("span", { class: card.kind === "credit" ? "mc plat" : "mc" }, [
    el("b", {}, [el("span", { class: "vis-hidden" }, [card.kind === "credit" ? "Credit card ending " : "Debit card ending "]), card.digits]),
  ]);
}

/** A limit meter: one ink bar (the proportion) and the words that carry the numbers. */
export function meter(m: NonNullable<Cell["meter"]>): HTMLElement {
  const node = el("span", { class: "meter" }, [
    el("span", { class: "tr", aria: { hidden: "true" } }, [el("i")]),
    el("small", {}, [el("b", { class: "money" }, [m.available]), " of ", el("span", { class: "money" }, [m.limit]), " available"]),
  ]);
  node.style.setProperty("--v", m.used);
  return node;
}

/** QA D8: the investments pill by its tone — a gain in the positive pill, a loss in the outlined one, 0.0% in neither (app.css 56). */
export const PILL = { up: "pill money", down: "pill neg money", flat: "pill flat money" } as const;

function row(initial: string, name: string, sub: string, figure: string, tone: string, status?: string): HTMLElement {
  return el("div", { class: "item" }, [
    el("span", { class: "ic", aria: { hidden: "true" } }, [initial]),
    // P1-6 (DCTA-192): Recent's "Held until 13 Oct 09:00" never breaks inside its date.
    el("span", { class: "tx" }, [el("b", {}, [name]), el("small", {}, [...keepTogether(sub), ...(status === undefined ? [] : [" · ", el("span", { class: "st" }, [status])])])]),
    el("span", { class: tone === "" ? "amt money" : `amt money ${tone}` }, [figure]),
  ]);
}
